import { Tooltip } from '@ttab/elephant-ui'
import { cn } from '@ttab/elephant-ui/utils'
import type { JSX } from 'react'
import { shortenNames } from '@/lib/shortenNames'

/**
 * The names held by an editor surface field. Up to three are shown, a longer
 * list shows two and collapses the rest into a count that reveals them on
 * hover, so a long list never pushes the pill across the editor.
 *
 * A lead is shown ahead of the names when something else is what gets
 * published, the organisation in a byline. The names follow it in parentheses,
 * muted, with the hint saying what they are still used for.
 */
export const PillNames = ({ names, placeholder, lead, hint }: {
  names: string[]
  placeholder: string
  lead?: string
  hint?: string
}): JSX.Element => {
  if (!names.length && !lead) {
    return <span className='text-sm text-muted-foreground pl-2'>{placeholder}</span>
  }

  const { shown: visibleNames, hiddenCount } = shortenNames(names)

  const visible = visibleNames.length
    ? (
        <span className={cn(lead && 'text-muted-foreground')}>
          {visibleNames.join(', ')}
        </span>
      )
    : placeholder ? <span className='text-muted-foreground'>{placeholder}</span> : null

  const count = hiddenCount > 0 && (
    <Tooltip
      content={(
        <div className='flex flex-col'>
          {names.map((name, index) => (
            <span key={index} className='text-xs px-1 py-0.5'>{name}</span>
          ))}
        </div>
      )}
    >
      <span className='font-semibold text-muted-foreground'>
        {`+${hiddenCount}`}
      </span>
    </Tooltip>
  )

  const named = hint && visible ? <Tooltip content={hint}>{visible}</Tooltip> : visible

  // text-sm on the row, not just the names: the tooltip triggers wrap the names
  // and the count in spans of their own, which would otherwise keep the taller
  // base line height
  return (
    <div className='flex flex-row items-center gap-1 cursor-default pl-2 text-sm'>
      {!!lead && <span>{lead}</span>}

      {lead && visibleNames.length
        ? (
            <span className='flex flex-row items-center text-muted-foreground'>
              (
              {named}
              {count && <span className='ps-1'>{count}</span>}
              )
            </span>
          )
        : (
            <>
              {named}
              {count}
            </>
          )}
    </div>
  )
}
